import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import DownloadAppCard from "./components/DownloadAppCard";
import API_BASE_URL from "./config";
import "./Dashboard.css";
import { clearAuthStorage } from "./auth/logout";

/**
 * The studio's home screen.
 *
 * It leads with whatever needs a decision — a photo quota about to stop an
 * upload, a job that failed, a client who has finished picking their photos —
 * because those are the things that cost something to find out late. The
 * engagement numbers below it are the studio's evidence that the galleries are
 * being used; the platform has always recorded them and never showed anyone.
 *
 * Every section hides itself when it has nothing to say, so a quiet account
 * looks calm rather than broken.
 */
function Dashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [studioName, setStudioName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [subscriptionBanner, setSubscriptionBanner] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") === "success") {
      setSubscriptionBanner(params.get("plan"));
      window.history.replaceState({}, document.title, "/dashboard");
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetch(`${API_BASE_URL}/api/user/${userId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((res) => {
          if (res) {
            const u = res.user || res;
            setStudioName(u.studio_name || "");
            if (u.logo_key || u.logo_url) setLogoUrl(`${API_BASE_URL}/api/user/${userId}/logo`);
          }
        })
        .catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return setLoading(false);
    fetch(`${API_BASE_URL}/api/studio/${userId}/dashboard`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      // Only render a payload that is actually a dashboard. A 200 carrying
      // something else — an older server, a proxy's error page — would
      // otherwise take the whole page down with it, and the point of this
      // screen is to still show the shortcuts when the numbers are missing.
      .then((res) => setData(isDashboardPayload(res) ? res : null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  const userEmail = localStorage.getItem("userEmail") || "user@example.com";
  const attention = data?.attention || [];
  const activity = data?.activity;
  const hasActivity = activity && (activity.searches || activity.views || activity.downloads);

  return (
    <div className="dashboard-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="main-content">
        <header className="dashboard-header">
          <div className="header-left">
            <button className="toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            {logoUrl && <img src={logoUrl} alt="Logo" className="header-logo" />}
            <h1 className="page-title">{studioName || "Photentra Dashboard"}</h1>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-email">{userEmail}</span>
              <button className="logout-btn" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </header>

        <main className="dashboard-main">
          <DownloadAppCard />

          {subscriptionBanner && (
            <div className="db-banner">
              <span>🎉 Subscription activated! You're now on the <strong>{subscriptionBanner}</strong> plan.</span>
              <button onClick={() => setSubscriptionBanner(null)} aria-label="Dismiss">✕</button>
            </div>
          )}

          {attention.length > 0 && (
            <section className="db-attention">
              <h2>Needs your attention</h2>
              <ul>
                {attention.map((item, i) => (
                  <li key={i} data-severity={item.severity} onClick={() => item.href && navigate(item.href)}>
                    <span className="db-dot" />
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data && (
            <section className="db-stats">
              <Stat label="Events" value={data.totals.events ?? 0} />
              <Stat label="Photos stored" value={data.totals.photos ?? 0} />
              <Stat label="Storage used" value={formatBytes(data.plan.storageUsedBytes)} sub={data.plan.storageMaxBytes ? `of ${formatBytes(data.plan.storageMaxBytes)}` : "unlimited"} />
              <Stat label="Plan" value={planLabel(data.plan.name)} sub={data.plan.daysRemaining != null ? `${data.plan.daysRemaining} days left` : null} />
            </section>
          )}

          {hasActivity && (
            <section className="db-panel">
              <h2>Guests, last {data.windowDays || 30} days</h2>
              <div className="db-activity">
                <Stat
                  label="Face searches"
                  value={activity.searches}
                  sub={activity.searches ? `${Math.round((activity.searchesMatched / activity.searches) * 100)}% found their photos` : null}
                />
                <Stat label="Gallery views" value={activity.views} />
                <Stat label="Downloads" value={activity.downloads} />
              </div>
            </section>
          )}

          {data?.recentEvents?.length > 0 && (
            <section className="db-panel">
              <h2>Recent events</h2>
              <ul className="db-events">
                {data.recentEvents.map((e) => (
                  <li key={e.id} onClick={() => navigate(`/events/${e.id}`)}>
                    <div className="db-event-name">
                      <strong>{e.name}</strong>
                      {e.date && <span>{new Date(e.date).toLocaleDateString()}</span>}
                    </div>
                    <div className="db-event-count">
                      {e.photoCount.toLocaleString()} photo{e.photoCount === 1 ? "" : "s"}
                      {e.quotaRatio != null && e.quotaRatio >= 0.8 && (
                        <span className="db-quota-flag">{Math.round(e.quotaRatio * 100)}% of limit</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!loading && !data && (
            <div className="dashboard-welcome">
              <h2>Welcome to {studioName || "Photentra"}</h2>
              <p>AI Powered Photo Platform</p>
            </div>
          )}

          <div className="dashboard-cards">
            <div className="card" onClick={() => navigate("/events")}>
              <h3>🎉 Events</h3>
              <p>Create and manage your events</p>
            </div>
            <div className="card" onClick={() => navigate("/portfolio")}>
              <h3>📁 Portfolio</h3>
              <p>Showcase your best work</p>
            </div>
            <div className="card" onClick={() => navigate("/wallet")}>
              <h3>💰 Wallet</h3>
              <p>Manage payments and balance</p>
            </div>
          </div>
        </main>

        <footer className="dashboard-footer">
          <p>&copy; 2026 Photentra. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

/** The fields the page reads before it renders anything. */
function isDashboardPayload(res) {
  return Boolean(res && res.totals && res.plan && res.activity);
}

function Stat({ label, value, sub }) {
  return (
    <div className="db-stat">
      <div className="db-stat-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div className="db-stat-label">{label}</div>
      {sub && <div className="db-stat-sub">{sub}</div>}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "0 GB";
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / 1024 ** 2).toFixed(0)} MB`;
}

function planLabel(slug) {
  if (!slug) return "Free";
  return slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default Dashboard;
